import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import i18n from "../i18n";
import Utils from "../utils/Utils";

const { serverURL } = Utils;

const initialState = {
  postComments: {},
  isAlertMessage: false,
  alertMessage: "",
  alertMessageType: "",
};

export const newComment = createAsyncThunk(
  "Comments/newComment",
  async (comment) => {
    const response = await fetch(`${serverURL}/post/comment/new`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(comment),
    });
    if (!response.ok) throw new Error(await response.text());
    const createdComment = await response.json();
    return createdComment;
  }
);
export const deleteComment = createAsyncThunk(
  "postComments/deleteComment",
  async (comment) => {
    const response = await fetch(`${serverURL}/post/comment/delete`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(comment),
    });
    if (!response.ok) throw new Error(await response.text());
    return comment;
  }
);
export const fetchComments = createAsyncThunk(
  "postComments/fetchComments",
  async ({ postID }) => {
    const response = await fetch(`${serverURL}/post/comments`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ postID }),
    });
    if (!response.ok) throw new Error(await response.text());
    const comments = await response.json();
    return { comments, postID };
  }
);
export const addCommentReaction = createAsyncThunk(
  "postComments/addCommentReaction",
  async ({ comment, type, userID }) => {
    const reaction = {
      commentID: comment._id,
      type,
      userID,
    };
    const response = await fetch(`${serverURL}/post/comment/react`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(reaction),
      credentials: "include",
    });
    if (!response.ok) throw new Error(await response.text());
    return { type, comment };
  }
);

export const commentsSlice = createSlice({
  name: "postComments",
  initialState,
  reducers: {
    setAlertMessage: (state, action) => {
      state.alertMessage = action.payload.message;
      state.alertMessageType = action.payload.type;
      state.isAlertMessage = action.payload.show;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(newComment.fulfilled, (state, action) => {
      const prevComments = state.postComments[action.payload.postID].comments;
      if (action.payload.replyTo) {
        const replyingComment = state.postComments[
          action.payload.postID
        ].comments.find((c) => c._id === action.payload.replyTo);
        if (replyingComment.hasOwnProperty("replies")) {
          replyingComment.replies = [action.payload].concat(
            replyingComment.replies
          );
        } else {
          replyingComment.replies = [action.payload];
        }
      } else
        state.postComments[action.payload.postID].comments = [
          action.payload,
          ...prevComments,
        ];
    });
    builder.addCase(newComment.rejected, (state, action) => {
      state.alertMessage = `${i18n.t("failedToComment")} ${
        action.error.message
      }`;
      state.alertMessageType = "error";
      state.isAlertMessage = true;
    });
    builder.addCase(deleteComment.fulfilled, (state, action) => {
      const prevComments = state.postComments[action.payload.postID].comments;
      if (action.payload.replyTo) {
        const replyingComment = state.postComments[
          action.payload.postID
        ].comments.find((c) => c._id === action.payload.replyTo);
        replyingComment.replies = replyingComment.replies.filter(
          (c) => c._id !== action.payload._id
        );
      } else
        state.postComments[action.payload.postID].comments =
          prevComments.filter((comment) => comment._id !== action.payload._id);
    });
    builder.addCase(deleteComment.rejected, (state, action) => {
      state.alertMessage = `${i18n.t("failedToDeleteComment")} ${
        action.error.message
      }`;
      state.alertMessageType = "error";
      state.isAlertMessage = true;
    });
    builder.addCase(fetchComments.pending, (state, action) => {
      const { postID } = action.meta.arg;
      if (!state.postComments.hasOwnProperty(postID))
        state.postComments[postID] = { status: "loading" };
    });
    builder.addCase(fetchComments.fulfilled, (state, action) => {
      const { comments, postID } = action.payload;
      state.postComments[postID].comments = comments;
      state.postComments[postID].status = "success";
    });
    builder.addCase(fetchComments.rejected, (state, action) => {
      const { postID } = action.meta.arg;
      state.postComments[postID].status = "failed";
      state.alertMessage = `${i18n.t("failedToFetchComment")} ${
        action.error.message
      }`;
      state.alertMessageType = "error";
      state.isAlertMessage = true;
    });
    builder.addCase(addCommentReaction.fulfilled, (state, action) => {
      const { comment, type } = action.payload;
      let com;
      if (comment.replyTo) {
        com = state.postComments[comment.postID].comments
          .find((c) => c._id === comment.replyTo)
          .replies.find((c) => c._id === comment._id);
      } else {
        com = state.postComments[comment.postID].comments.find(
          (c) => c._id === comment._id
        );
      }
      if (!com.userReaction && type) com.reactionCount += 1;
      else if (com.userReaction && !type) com.reactionCount -= 1;
      com.userReaction = type;
    });
    builder.addCase(addCommentReaction.rejected, (state, action) => {
      state.isAlertMessage = true;
      state.alertMessage = `${i18n.t("failedToReact")} ${action.error.message}`;
      state.alertMessageType = "error";
    });
  },
});

export default commentsSlice.reducer;
export const { setAlertMessage } = commentsSlice.actions;
